import { DataSource } from '@angular/cdk/table';
import { FolderNode } from './folder-node';
import { FolderSdk } from 'src/app/sdks/folder.sdk';
import { FlatTreeControl } from '@angular/cdk/tree';
import { BehaviorSubject, merge, Observable } from 'rxjs';
import { CollectionViewer, SelectionChange } from '@angular/cdk/collections';
import { map } from 'rxjs/operators';
import { FolderResource } from 'src/app/models/resource/folder.resource';

export class FolderDataSource implements DataSource<FolderNode> {

    dataChange = new BehaviorSubject<FolderNode[]>([]);

    get data(): FolderNode[] { return this.dataChange.value; }
    set data(value: FolderNode[]) {
        this.treeControl.dataNodes = value;
        this.dataChange.next(value);
    }

    constructor(private treeControl: FlatTreeControl<FolderNode>, private folderService: FolderSdk) { }

    connect(collectionViewer: CollectionViewer): Observable<FolderNode[] | readonly FolderNode[]> {
        this.treeControl.expansionModel.changed.subscribe(change => {
            if ((change as SelectionChange<FolderNode>).added ||
                (change as SelectionChange<FolderNode>).removed) {
                this.handleTreeControl(change as SelectionChange<FolderNode>);
            }
        });

        return merge(collectionViewer.viewChange, this.dataChange).pipe(map(() => this.data));
    }

    disconnect(): void {
		// Intentionally Blank
	}

    /** Handle expand/collapse behaviors */
    handleTreeControl(change: SelectionChange<FolderNode>) {
        if (change.added) {
            change.added.forEach(node => this.toggleNode(node, true));
        }
        if (change.removed) {
            change.removed.slice().reverse().forEach(node => this.toggleNode(node, false));
        }
    }

    /**
     * Toggle the node, remove from display list
     */
    toggleNode(node: FolderNode, expand: boolean) {
        node.isLoading = true;

        const index = this.data.indexOf(node);
        if (expand) {
            this.folderService.getChildrenOf(node.folder.folderId).subscribe(data => {
                const children = data.value;
                if (children.length === 0 || index < 0) {
                    console.warn('Unknown node or no children found for: ' + node.folder.folderName);
                } else {
                    const nodes = [];
                    children.forEach(child => {
                        const newChild = new FolderNode(child, node.level + 1);
                        newChild.updateDocCount(this.folderService);
                        nodes.push(newChild);
                    });
                    this.data.splice(index + 1, 0, ...nodes);
                }

                this.dataChange.next(this.data);
                node.isLoading = false;
            });
        } else {
            let count = 0;
			// eslint-disable-next-line no-empty
            for (let i = index + 1; i < this.data.length && this.data[i].level > node.level; i++, count++) { }
            this.data.splice(index + 1, count);

            this.dataChange.next(this.data);
            node.isLoading = false;
        }
    }

    /**
     * Retrieves the path to the specified node as a set of IFolders excluding the root.
     */
    getPath(node: FolderNode): FolderResource[] {
        const path: FolderResource[] = [node.folder];

        const index = this.data.indexOf(node);
        let prevLevel = node.level;

        // Reverse through the set of nodes starting at the node before the one specified (specified node has already been added)
        // Anytime we reach an upper level, we've reached a parent - add it look for the next one
        for (let i = index - 1; i >= 0; i--) {
            const level = this.data[i].level;
            if (level === 0) break; // We've reached the root
            if (level === prevLevel - 1) {
                path.push(this.data[i].folder);
                prevLevel = level;
            }
        }

        return path.reverse();
    }
}
